import React, { useState, useEffect, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import styled from "styled-components";
import { apiService } from "../services/api";
import { FiShare2, FiX } from "react-icons/fi";
import { IoIosArrowForward } from "react-icons/io";
import { FaGasPump, FaBatteryFull, FaBolt } from "react-icons/fa";
import Carousel from "react-bootstrap/Carousel";
import {
  FaWhatsapp,
  FaFacebook,
  FaEnvelope,
  FaUniversity,
  FaTag,
} from "react-icons/fa";
import SEO from "../components/SEO";
import { generateProductSchema, generateBreadcrumbSchema, generateMetaDescription, generateMetaKeywords } from "../utils/seoHelpers";
import { AiOutlineArrowRight, AiOutlineArrowLeft } from "react-icons/ai";

const ProductDetailWrapper = styled.div`
  background: white;
  min-height: 100vh;
  padding: 0;

  @media (max-width: 768px) {
    padding: 0 0 80px 0; /* Add bottom padding to account for fixed buttons */
  }

  @media (max-width: 480px) {
    padding: 0 0 80px 0;
  }
`;

const Container = styled.div`
  max-width: 1300px;
  margin: 0 auto;
  padding: 0;
  background: white;
`;

const Breadcrumb = styled.nav`
  padding: 16px 24px;
  background: white;
  border-bottom: 1px solid #e0e0e0;
  font-size: 14px;
  color: #666;
  display: flex;
  align-items: center;
  gap: 8px;

  a {
    color: #666;
    text-decoration: none;

    &:hover {
      color: #ff6720;
    }
  }

  span {
    color: #ccc;

    &.separator {
      margin: 0 4px;
    }
  }
`;

const MainContent = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  min-height: calc(100vh - 200px);

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const ImageSection = styled.div`
  display: grid;
  grid-template-columns: auto 1fr;
  grid-template-areas:
    "thumbnails main"
    "thumbnails buttons";
  gap: 16px;
  padding: 24px;
  background: #ffffffff;
  position: sticky;
  top: 0;
  height: auto;
  align-self: flex-start;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    grid-template-areas:
      "main"
      "thumbnails"
      "buttons";
    border-right: none;
    border-bottom: 1px solid #e0e0e0;
    position: static;
    height: auto;
    align-self: auto;
  }
`;

const ThumbnailList = styled.div`
  grid-area: thumbnails;
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 80px;

  @media (max-width: 1024px) {
    flex-direction: row;
    width: auto;
    justify-content: center;
  }

  @media (max-width: 480px) {
    justify-content: flex-start;
  }
`;

const Thumbnail = styled.div`
  width: 72px;
  height: 72px;
  border: 2px solid ${(props) => (props.active ? "#ff6720" : "#e0e0e0")};
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.3s ease;
  background: white;

  &:hover {
    border-color: #ff6720;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
`;

const MainImageContainer = styled.div`
  grid-area: main;
  height: 40vh;
  display: flex;
  flex-direction: column;
  gap: 10px;

  @media (max-width: 480px) {
    height: 300px;
  }
`;

const MainImage = styled.div`
  width: 100% !important;
  max-width: 500px;
  aspect-ratio: 1;
  overflow: hidden;
  background: white;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
`;

const VideoContainer = styled.div`
  width: 100% !important;
  max-width: 500px;
  aspect-ratio: 16/9;
  overflow: hidden;
  background: #000;
  border-radius: 8px;

  iframe,
  video {
    width: 100%;
    height: 100%;
    border: none;
    border-radius: 8px;
  }
`;

const ProductInfo = styled.div`
  padding: 32px 24px;
  display: flex;
  border-left: 1px solid #e0e0e0;
  flex-direction: column;
  gap: 24px;
`;

const ProductHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
`;

const TitleSection = styled.div`
  flex: 1;
`;

const ProductTitle = styled.h1`
  font-size: 28px;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0 0 8px 0;
  line-height: 1.3;

  @media (max-width: 768px) {
    font-size: 24px;
  }
`;

const ProductBrand = styled.div`
  color: #ff6720;
  font-size: 16px;
  font-style: italic;
  font-weight: 600;
  margin-bottom: 12px;
`;

const ActionButtons = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: flex-end;

  @media (max-width: 480px) {
    display: none;
  }
`;

const ActionButtons1 = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: flex-end;
  display: none;

  @media (max-width: 480px) {
    display: flex;
    flex-direction: row;
    width: 100%;
    justify-content: flex-end !important;
    align-items: center;
  }
`;

const ShareButton = styled.button`
  background: none;
  border: 1px solid #ddd;
  padding: 8px 12px;
  border-radius: 6px;
  cursor: pointer;
  color: #666;
  font-size: 14px;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 6px;

  &:hover {
    border-color: #ff6720;
    color: #ff6720;
  }
`;

const SharePopup = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ShareModal = styled.div`
  background: white;
  border-radius: 12px;
  padding: 24px;
  max-width: 400px;
  width: 90%;
  position: relative;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
`;

const ShareHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const ShareTitle = styled.h3`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #333;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 20px;
  cursor: pointer;
  color: #666;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: #333;
  }
`;

const ShareOptions = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
`;

const ShareOption = styled.button`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  background: white;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s ease;
  position: relative;

  &:hover {
    background: #f5f5f5;
    border-color: #ccc;
  }

  &.whatsapp:hover {
    background: #e8f5e8;
    border-color: #25d366;
    color: #25d366;
  }

  &.facebook:hover {
    background: #e7f3ff;
    border-color: #1877f2;
    color: #1877f2;
  }

  &.message:hover {
    background: #fff7ed;
    border-color: #f97316;
    color: #f97316;
  }
`;

const ShareIcon = styled.div`
  font-size: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const HighlightsSection = styled.div`
  margin-bottom: 24px;
`;

const HighlightsTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin-bottom: 16px;
`;

const TechnicalSpecsTable = styled.div`
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  overflow: hidden;
  margin-top: 16px;
`;

const SpecRow = styled.div`
  display: flex;
  border-bottom: 1px solid #e5e5e5;

  &:last-child {
    border-bottom: none;
  }

  &:nth-child(odd) {
    background-color: #f8f9fa;
  }

  &:nth-child(even) {
    background-color: #ffffff;
  }
`;

const SpecLabel = styled.div`
  flex: 1;
  padding: 16px 20px;
  font-weight: 500;
  color: #333;
  background-color: rgba(229, 229, 229, 0.3);
  border-right: 1px solid #e5e5e5;
  font-size: 14px;
  line-height: 1.4;
`;

const SpecValue = styled.div`
  flex: 2;
  padding: 16px 20px;
  color: #666;
  font-size: 14px;
  line-height: 1.4;
  font-weight: 400;
`;

const CollapsibleSection = styled.div`
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  margin: 20px 0;
  background: #fff;
`;

const CollapsibleHeader = styled.div`
  display: flex;
  justify-content: between;
  align-items: center;
  padding: 16px 20px;
  cursor: pointer;
  user-select: none;
  border-bottom: ${(props) => (props.isOpen ? "1px solid #e5e5e5" : "none")};

  &:hover {
    background-color: #f8f9fa;
  }
`;

const CollapsibleTitle = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #333;
  flex-grow: 1;
`;

const CollapsibleIcon = styled.div`
  font-size: 18px;
  color: #666;
  transition: transform 0.3s ease;
  transform: ${(props) => (props.isOpen ? "rotate(45deg)" : "rotate(0deg)")};
`;

const CollapsibleContent = styled.div`
  padding: ${(props) => (props.isOpen ? "20px" : "0 20px")};
  max-height: ${(props) => (props.isOpen ? "1000px" : "0")};
  overflow: hidden;
  transition:
    max-height 0.3s ease,
    padding 0.3s ease;
`;

const FeatureGrid = styled.div`
  display: flex;
  gap: 16px;

  @media (min-width: 768px) {
    grid-template-columns: 1fr 1fr;
  }
`;

const FeatureItem = styled.div`
  padding: 16px;
  width: 100% !important;
  background: #f8f9fa;
  border-radius: 8px;
  border-left: 4px solid #ff6720;
`;

const FeatureTitle = styled.h4`
  margin: 0 0 8px 0;
  font-size: 14px;
  font-weight: 600;
  color: #333;
`;

const FeatureDescription = styled.p`
  margin: 0;
  font-size: 13px;
  color: #666;
  line-height: 1.5;
`;

const PaymentSection = styled.div`
  background: #f8f9fa;
  padding: 20px;
  border-radius: 8px;
  margin-bottom: 24px;
`;

const PaymentTitle = styled.h4`
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin-bottom: 12px;
`;

const PaymentOptions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const PaymentOption = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: #666;
  font-size: 14px;

  .value {
    color: #333;
    font-weight: 500;
  }
`;

const PaymentOptionWithIcon = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const PaymentIcon = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f8f9fa;
  border: 1px solid #e5e5e5;

  img {
    width: 28px;
    height: 28px;
    object-fit: contain;
    border-radius: 2px;
  }
`;

const BranchMapModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: 20px;
`;

const BranchMapContent = styled.div`
  background: white;
  border-radius: 12px;
  width: 100%;
  max-width: 1200px;
  max-height: 90vh;
  overflow: hidden;
  position: relative;
  display: flex;
  flex-direction: column;
`;

const BranchMapHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  border-bottom: 1px solid #e5e5e5;
`;

const BranchMapTitle = styled.h2`
  margin: 0;
  font-size: 24px;
  font-weight: 600;
  color: #333;
`;

const BranchMapClose = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #666;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;

  &:hover {
    background: #f5f5f5;
    color: #333;
  }
`;

const BranchMapBodyContainer = styled.div`
  display: flex;
  height: 600px;

  @media (max-width: 768px) {
    flex-direction: column;
    height: auto;
  }
`;

const BranchMapContainer = styled.div`
  flex: 1;
  position: relative;

  @media (max-width: 768px) {
    height: 300px;
  }
`;

const BranchList = styled.div`
  width: 400px;
  padding: 20px;
  overflow-y: auto;
  border-left: 1px solid #e5e5e5;
  background: #f8f9fa;

  @media (max-width: 768px) {
    width: 100%;
    height: 300px;
    border-left: none;
    border-top: 1px solid #e5e5e5;
  }
`;

const BranchCardTop = styled.div`
  display: flex;
  gap: 12px;
  align-items: flex-start;
`;

const BranchImageContainer = styled.div`
  width: 120px;
  height: 100px;
  flex-shrink: 0;
  border-radius: 8px;
  overflow: hidden;
  background: #f5f5f5;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  @media (max-width: 768px) {
    width: 100px;
    height: 80px;
  }
`;

const BranchInfoContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const BranchItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
  border-bottom: 1px solid #f0f0f0;
  background: white;
  border-radius: 8px;
  margin-bottom: 12px;

  &:last-child {
    border-bottom: none;
  }
`;

const BranchName = styled.h4`
  margin: 0 0 6px 0;
  font-size: 16px;
  font-weight: 600;
  color: #333;
  line-height: 1.3;
`;

const BranchAddress = styled.p`
  margin: 0;
  font-size: 13px;
  color: #666;
  line-height: 1.4;
`;

const BranchPhone = styled.a`
  margin: 4px 0 0 0;
  font-size: 14px;
  color: #25d366;
  font-weight: 600;
  text-decoration: none;
  display: flex;
  align-items: center;
  gap: 6px;
  width: fit-content;
  transition: all 0.2s ease;

  &:hover {
    color: #128c7e;
    transform: translateX(2px);
  }

  svg {
    font-size: 16px;
  }
`;

const BranchButtonsContainer = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 12px;
`;

const BranchActionButton = styled.button`
  background: ${(props) => (props.primary ? "#ff6720" : "transparent")};
  color: ${(props) => (props.primary ? "white" : "#ff6720")};
  border: 1px solid #ff6720;
  padding: 10px 16px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  flex: 1;

  &:hover {
    background: ${(props) => (props.primary ? "#e55a1c" : "#ff6720")};
    color: white;
  }
`;

const ActionButtonsSection = styled.div`
  grid-area: buttons;
  display: flex;
  flex-direction: row;
  gap: 12px;
  height: fit-content;

  @media (max-width: 768px) {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    flex-direction: row;
    padding: 10px 20px;
    background: white;
    box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.1);
    z-index: 10;
    margin-top: 0;
    gap: 10px;
  }

  @media (max-width: 480px) {
    flex-direction: row;
    gap: 8px;
    padding: 10px;
  }
`;

const ContactButton = styled.button`
  background: #ff6720;
  color: white;
  border: none;
  padding: 16px 24px;
  height: 64px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  width: 100%;

  &:hover {
    background: #e55a1c;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(255, 103, 32, 0.3);
  }
`;

const FindBranchButton = styled.button`
  background: white;
  color: #333;
  border: 2px solid #333;
  padding: 16px 24px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  width: 100%;
  height: 64px;

  &:hover {
    background: #333;
    color: white;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  }
`;

const SellerName = styled.div`
  color: #494949ff;
  font-weight: 600;
  font-size: 16px;
`;

const SellerBadge = styled.img`
  height: 24px;
  width: auto;
`;

const LoadingSpinner = styled.div`
  text-align: center;
  padding: 80px 20px;
  color: #666;
  font-size: 18px;
`;

const NotFound = styled.div`
  text-align: center;
  padding: 80px 20px;
  color: #666;

  h2 {
    font-size: 28px;
    margin-bottom: 15px;
    color: #333;
  }

  p {
    font-size: 16px;
    margin-bottom: 30px;
  }
`;

const BackLink = styled(Link)`
  display: inline-block;
  background: #ff6720;
  color: white;
  padding: 12px 24px;
  border-radius: 4px;
  text-decoration: none;
  font-weight: 600;

  &:hover {
    background: #e55a1c;
  }
`;

const CarouselWrapper = styled.div`
  padding: 40px 0;
  border-top: 1px solid #e0e0e0;
  position: relative;
  max-width: 1300px !important;

  .carousel-control-prev,
  .carousel-control-next {
    width: 40px;
    height: 40px;
    background: #ff6720;
    border-radius: 50%;
    top: 50%;
    transform: translateY(-50%);
    transition: all 0.3s ease;

    &:hover {
      background: #e55a1c;
    }

    @media (max-width: 768px) {
      display: none; /* Hide desktop carousel controls on mobile */
    }
  }

  .carousel-control-prev {
    left: 0px;
  }

  .carousel-control-next {
    right: 0px;
  }

  .carousel-indicators {
    bottom: -40px;

    button {
      background-color: #ff6720;
      width: 10px;
      height: 10px;
      border-radius: 50%;
      margin: 0 5px;
    }

    .active {
      background-color: #e55a1c;
    }

    @media (max-width: 768px) {
      display: none; /* Hide carousel indicators on mobile */
    }
  }

  /* Hide desktop carousel on mobile */
  @media (max-width: 768px) {
    .carousel {
      display: none;
    }
  }
`;

const DesktopCarouselOnly = styled.div`
  @media (max-width: 768px) {
    display: none;
  }
`;

// Mobile-specific carousel components (like HomePage)
const MobileCarouselWrapper = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: block;
    position: relative;
    overflow: hidden;
    padding: 10px 10px; /* Negative margin to allow padding inside scroll container */
  }
`;

const MobileScrollContainer = styled.div`
  @media (max-width: 768px) {
    display: flex;
    overflow-x: auto;
    scroll-behavior: auto;
    gap: 15px;
    padding: 10px 10px; /* Add left and right padding */
    scrollbar-width: none;
    -ms-overflow-style: none;

    &::-webkit-scrollbar {
      display: none;
    }
  }
`;

const MobileProductCard = styled.div`
  @media (max-width: 768px) {
    min-width: calc(50% - 27.5px); /* Show 2 cards: 50% width minus half of gap and padding */
    flex-shrink: 0;
    background: white;
    border-radius: 8px;
    overflow: hidden;
    border: 1px solid #e5e5e5;
    transition: all 0.3s ease;
    display: flex;
    flex-direction: column;

    &:hover {
      transform: translateY(-5px);
      box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
    }
  }

  @media (max-width: 480px) {
    width: 250px;
  }
`;

const MobileProgressBarContainer = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 15px;
    margin-top: 20px;
    padding: 0 20px; /* Match the scroll container padding */
  }
`;

const MobileProgressBar = styled.div`
  position: relative;
  width: 100%;
  height: 2px;
  background-color: #e0e0e0;
  border-radius: 2px;
`;

const MobileProgressFill = styled.div`
  position: absolute;
  height: 100%;
  width: 30px;
  background-color: #333;
  border-radius: 2px;
  transition: left 0.3s ease;
  left: ${(props) => `calc((100% - 30px) * ${props.progress / 100})`};
`;

const MobileArrowButton = styled.button`
  width: 36px;
  height: 36px;
  background: transparent;
  color: #333;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 18px;
  border: none;

  &:hover:not(:disabled) {
    color: #ff6720;
  }

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
`;

const CarouselItemWrapper = styled.div`
  display: grid !important;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 20px;
  padding: 60px;
  justify-content: center;

  @media (max-width: 480px) {
    padding: 20px;
    grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  }
`;

const CarouselProductCard = styled.div`
  background: white;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #e5e5e5;
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
  }
`;

const RelatedTitle = styled.h2`
  font-size: 24px;
  font-weight: 600;
  color: #333;
  margin-bottom: 24px;
  text-align: center;
`;

const RelatedProductImage = styled.div`
  height: 200px;
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  padding-left: 10px;
  padding-right: 10px;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    transition: transform 0.3s ease;
  }

  &:hover img {
    transform: scale(1.05);
  }

  @media (max-width: 480px) {
    height: 120px;
  }
`;

const RelatedProductInfo = styled.div`
  padding: 16px;
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

const RelatedProductName = styled.h3`
  font-size: 16px;
  font-weight: bold;
  color: #333;
  margin-bottom: 8px;
  line-height: 1.3;
`;

const RelatedProductDescription = styled.p`
  font-size: 14px;
  color: #666;
  line-height: 1.4;
  margin-bottom: 12px;
  flex: 1;
`;

const RelatedViewButton = styled(Link)`
  display: block;
  text-align: center;
  padding: 10px 16px;
  background: #ff6720;
  color: white;
  border: none;
  border-radius: 50px;
  text-decoration: none;
  font-size: 14px;
  font-weight: 600;
  transition: all 0.3s ease;

  &:hover {
    background: #e55a1c;
  }
`;

const NoRelatedProducts = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: #666;
  font-size: 16px;
`;

const YouMayAlsoLikeTitle = styled.h2`
  font-size: 24px;
  font-weight: 600;
  color: #333;
  margin-bottom: 24px;
  text-align: center;
`;

const YouMayAlsoLikeImage = styled.div`
  height: 200px;
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  padding-left: 10px;
  padding-right: 10px;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    transition: transform 0.3s ease;
  }

  &:hover img {
    transform: scale(1.05);
  }

  @media (max-width: 480px) {
    height: 120px;
  }
`;

const YouMayAlsoLikeInfo = styled.div`
  padding: 16px;
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

const YouMayAlsoLikeName = styled.h3`
  font-size: 16px;
  font-weight: bold;
  color: #333;
  margin-bottom: 8px;
  line-height: 1.3;
`;

const YouMayAlsoLikeDescription = styled.p`
  font-size: 14px;
  color: #666;
  line-height: 1.4;
  margin-bottom: 12px;
  flex: 1;
`;

const YouMayAlsoLikeViewButton = styled(Link)`
  display: block;
  text-align: center;
  padding: 10px 16px;
  background: #ff6720;
  color: white;
  border: none;
  border-radius: 50px;
  text-decoration: none;
  font-size: 14px;
  font-weight: 600;
  transition: all 0.3s ease;

  &:hover {
    background: #e55a1c;
  }
`;

const NoYouMayAlsoLikeProducts = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: #666;
  font-size: 16px;
`;

const ProductDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [showSharePopup, setShowSharePopup] = useState(false);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [showBackButton, setShowBackButton] = useState(true);
  const [showDetailsFeatures, setShowDetailsFeatures] = useState(false);
  const [categoryProducts, setCategoryProducts] = useState([]);
  const [showBranchMap, setShowBranchMap] = useState(false);
  const [allProducts, setAllProducts] = useState([]);

  // Add these new state variables
  const [relatedScrollProgress, setRelatedScrollProgress] = useState(0);
  const [relatedShowLeftArrow, setRelatedShowLeftArrow] = useState(false);
  const [relatedShowRightArrow, setRelatedShowRightArrow] = useState(true);
  const relatedScrollRef = useRef(null);

  const [alsoLikeScrollProgress, setAlsoLikeScrollProgress] = useState(0);
  const [alsoLikeShowLeftArrow, setAlsoLikeShowLeftArrow] = useState(false);
  const [alsoLikeShowRightArrow, setAlsoLikeShowRightArrow] = useState(true);
  const alsoLikeScrollRef = useRef(null);

  // Scroll handlers for Related Products
  const handleRelatedScroll = () => {
    if (relatedScrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = relatedScrollRef.current;
      setRelatedShowLeftArrow(scrollLeft > 0);
      setRelatedShowRightArrow(scrollLeft < scrollWidth - clientWidth - 10);
      
      const maxScroll = scrollWidth - clientWidth;
      const progress = maxScroll > 0 ? (scrollLeft / maxScroll) * 100 : 0;
      setRelatedScrollProgress(progress);
    }
  };

  const scrollRelatedLeft = () => {
    if (relatedScrollRef.current) {
      const cardWidth = relatedScrollRef.current.querySelector('[class*="MobileProductCard"]')?.offsetWidth || 0;
      const gap = 15;
      relatedScrollRef.current.scrollBy({ left: -(cardWidth + gap), behavior: 'smooth' }); // Changed to smooth
      setTimeout(handleRelatedScroll, 300);
    }
  };

  const scrollRelatedRight = () => {
    if (relatedScrollRef.current) {
      const cardWidth = relatedScrollRef.current.querySelector('[class*="MobileProductCard"]')?.offsetWidth || 0;
      const gap = 15;
      relatedScrollRef.current.scrollBy({ left: (cardWidth + gap), behavior: 'smooth' }); // Changed to smooth
      setTimeout(handleRelatedScroll, 300);
    }
  };

  // Scroll handlers for You May Also Like
  const handleAlsoLikeScroll = () => {
    if (alsoLikeScrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = alsoLikeScrollRef.current;
      setAlsoLikeShowLeftArrow(scrollLeft > 0);
      setAlsoLikeShowRightArrow(scrollLeft < scrollWidth - clientWidth - 10);
      
      const maxScroll = scrollWidth - clientWidth;
      const progress = maxScroll > 0 ? (scrollLeft / maxScroll) * 100 : 0;
      setAlsoLikeScrollProgress(progress);
    }
  };

  const scrollAlsoLikeLeft = () => {
    if (alsoLikeScrollRef.current) {
      const cardWidth = alsoLikeScrollRef.current.querySelector('[class*="MobileProductCard"]')?.offsetWidth || 0;
      const gap = 15;
      alsoLikeScrollRef.current.scrollBy({ left: -(cardWidth + gap), behavior: 'smooth' }); // Changed to smooth
      setTimeout(handleAlsoLikeScroll, 300);
    }
  };

  const scrollAlsoLikeRight = () => {
    if (alsoLikeScrollRef.current) {
      const cardWidth = alsoLikeScrollRef.current.querySelector('[class*="MobileProductCard"]')?.offsetWidth || 0;
      const gap = 15;
      alsoLikeScrollRef.current.scrollBy({ left: (cardWidth + gap), behavior: 'smooth' }); // Changed to smooth
      setTimeout(handleAlsoLikeScroll, 300);
    }
  };

  useEffect(() => {
    // Always show back button since we'll handle navigation intelligently
    setShowBackButton(true);

    const fetchProduct = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await apiService.getProduct(id);

        if (response.success && response.data) {
          console.log("=== CURRENT PRODUCT DATA ===");
          console.log("Product:", response.data);
          console.log("Product category_id:", response.data.category_id);
          console.log("Product category_name:", response.data.category_name);

          setProduct(response.data);
          // Update page title with product name
          document.title = `${response.data.name} | XPLOD-GROUPS`;

          // Fetch category-specific products if product has a category
          if (response.data.category_id) {
            fetchCategoryProducts(response.data.category_id);
          }

          // Fetch all products for You May Also Like
          fetchAllProducts();
        } else {
          setError("Product not found");
        }
      } catch (err) {
        console.error("Error fetching product:", err);
        setError("Failed to load product");
      } finally {
        setLoading(false);
      }
    };

    const fetchCategoryProducts = async (categoryId) => {
      try {
        console.log("=== FETCHING CATEGORY PRODUCTS ===");
        console.log("Current product ID:", id);
        console.log("Looking for category_id:", categoryId);

        const response = await apiService.getProducts({
          category_id: categoryId,
          limit: 20,
          page: 1,
        });

        console.log("API Response:", response);

        if (response.success && response.data) {
          console.log(
            "All products returned:",
            response.data.map((p) => ({
              id: p.id,
              name: p.name,
              category_id: p.category_id,
              category_name: p.category_name,
            })),
          );

          // Filter out current product and take up to 8 products from the same category
          const filtered = response.data
            .filter((p) => {
              const shouldInclude =
                p.id !== id &&
                p.id !== parseInt(id) &&
                p.category_id === parseInt(categoryId);
              console.log(
                `Product ${p.name} (ID: ${p.id}, Category: ${p.category_id}) - Include: ${shouldInclude}`,
              );
              return shouldInclude;
            })
            .sort(() => 0.5 - Math.random())
            .slice(0, 8);

          console.log(
            "Filtered products:",
            filtered.map((p) => ({
              id: p.id,
              name: p.name,
              category_id: p.category_id,
              category_name: p.category_name,
            })),
          );

          setCategoryProducts(filtered);
        }
      } catch (err) {
        console.error("Error fetching category products:", err);
      }
    };

    const fetchAllProducts = async () => {
      try {
        console.log("=== FETCHING ALL PRODUCTS ===");
        const response = await apiService.getProducts({
          limit: 20,
          page: 1,
        });

        console.log("All Products API Response:", response);

        if (response.success && response.data) {
          // Filter out current product and shuffle
          const filtered = response.data
            .filter((p) => p.id !== id && p.id !== parseInt(id))
            .sort(() => 0.5 - Math.random())
            .slice(0, 8);

          console.log(
            "Shuffled all products:",
            filtered.map((p) => ({
              id: p.id,
              name: p.name,
              category_id: p.category_id,
              category_name: p.category_name,
            })),
          );

          setAllProducts(filtered);
        }
      } catch (err) {
        console.error("Error fetching all products:", err);
      }
    };

    if (id) {
      fetchProduct();
    }
  }, [id]);

  // Update page title when component unmounts
  useEffect(() => {
    return () => {
      document.title = "Home | XPLOD-GROUPS";
    };
  }, []);

  // Kerala branch locations
  const branches = [
    {
      id: 1,
      name: "STIHL XPLOD TOOLS",
      address: "SH8, Kumbazha, Mylapra, Pathanamthitta, Kerala",
      lat: 9.263078942697597, 
      lng: 76.80643844069269,
      phone: "+91 9446189273",
      email: "kochi@xplod-group.com",
      image: "https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=400&h=300&fit=crop"
    },
    {
      id: 2,
      name: "STIHL AGRITECH ",
      address:"PVMX+W6Q, Kazhakoottam - Kilimanoor Rd, Karette, Kerala",
      lat: 8.734849322127499,
      lng: 76.89801195204323,
      phone: "+91 98765 43211",
      email: "tvm@xplod-group.com",
      image: "https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=400&h=300&fit=crop"
    },
    {
      id: 3,
      name: "STIHL XPLOD AGROS",
      address: "Thengana-Thiruvanchoor Rd, Manarcadu, Kerala",
      lat: 9.589774023174282, 
      lng: 76.5827373312311,
      phone: "+91 9747452539",
      email: "kozhikode@xplod-group.com",
      image: "https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=400&h=300&fit=crop"
    },
    {
      id: 4,
      name: "STIHL MACHINE WORLD",
      address: "Oppostie to Soccer Turf, Near Vilakkudy Panchayath Office, Kunnicode, Kollam, Kerala",
      lat: 9.028736387150614,
      lng: 76.85693904623051,
      phone: "+91 88480 64407",
      email: "kozhikode@xplod-group.com",
      image: "https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=400&h=300&fit=crop"
    },
    {
      id: 5,
      name: "STIHL MOTO AGRO",
      address: "5P4R+PGC, Adoor, Kerala",
      lat: 9.15660365220432, 
      lng: 76.74121119078215,
      phone: "+91 9747459993",
      email: "thrissur@xplod-group.com",
      image: "https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=400&h=300&fit=crop"
    },
    {
      id: 6,
      name: "STIHL AGRITECH ARYANAD",
      address: "Parthasaradhi, Agritech, Building Tholoor, PO, Cherukulam, Aryanad, Kerala",
      lat: 8.5816154484634, 
      lng: 77.08112080971321,
      phone: "+91 9747459993",
      email: "thrissur@xplod-group.com",
      image: "https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=400&h=300&fit=crop"
    },
    {
      id: 7,
      name: "STIHL AGRI MACHINE HUB",
      address: "MC Rd, opp. indian oil pump, Mulakuzha, Kerala",
      lat: 9.295633609996818, 
      lng: 76.64260124040051,
      phone: "+91 8714870868",
      email: "thrissur@xplod-group.com",
      image: "https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=400&h=300&fit=crop"
    },
    {
      id: 8,
      name: "STIHL MOTO AGRO",
      address: "Kavinadathu Arcade. opp.S C middle school,Pazhavangadi, Ranni, Kerala",
      lat: 9.390123196666194, 
      lng: 76.78809989622404,
      phone: "+91 8281816762",
      email: "thrissur@xplod-group.com",
      image: "https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=400&h=300&fit=crop"
    },
    {
      id: 9,
      name: "STIHL MOTO AGRO",
      address: "MAVILA BUILDING MALLAPPALLY EAST, near Mallappally Bridge, Mallappally, Kerala",
      lat: 9.451646843167138, 
      lng: 76.66075176447376,
      phone: "+91 8281817562",
      email: "thrissur@xplod-group.com",
      image: "https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=400&h=300&fit=crop"
    },
    {
      id: 10,
      name: "STIHL XPLOD AGROS",
      address: "Koothattukulam - Ramapuram Rd, Ramapuram, Kerala",
      lat: 9.812681848465482, 
      lng: 76.6414855288354,
      phone: "+91 8281817562",
      email: "thrissur@xplod-group.com",
      image: "https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=400&h=300&fit=crop"
    },
];

  useEffect(() => {
    if (showBranchMap) {
      // Load OpenStreetMap
      const script = document.createElement("script");
      script.src = "https://unpkg.com/leaflet@1.7.1/dist/leaflet.js";
      script.onload = () => initializeMap();
      document.head.appendChild(script);

      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = "https://unpkg.com/leaflet@1.7.1/dist/leaflet.css";
      document.head.appendChild(link);

      return () => {
        document.head.removeChild(script);
        document.head.removeChild(link);
      };
    }
  }, [showBranchMap]);

  useEffect(() => {
    if (product) {
      const productMedia = getProductMedia(product);
      const productVideos = productMedia.videos;
      // Set selectedImageIndex to the first image (after videos)
      setSelectedImageIndex(productVideos.length);
    }
  }, [product]);

  const initializeMap = () => {
    if (typeof window.L === "undefined") return;

    const mapContainer = document.getElementById("branch-map");
    if (!mapContainer) return;

    // Clear any existing map
    mapContainer.innerHTML = "";

    // Initialize map
    const map = window.L.map("branch-map").setView([10.0, 76.5], 8);

    // Use OpenStreetMap with better detail
    window.L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: "© OpenStreetMap contributors",
      maxZoom: 19,
    }).addTo(map);

    // Create custom orange marker icon
    const orangeIcon = window.L.icon({
      iconUrl:
        "data:image/svg+xml;base64," +
        btoa(`
      <svg width="25" height="41" viewBox="0 0 25 41" xmlns="http://www.w3.org/2000/svg">
        <path d="M12.5 0C5.596 0 0 5.596 0 12.5c0 12.5 12.5 28.5 12.5 28.5s12.5-16 12.5-28.5C25 5.596 19.404 0 12.5 0z" fill="#ff6720" stroke="#ffffff" stroke-width="2"/>
        <circle cx="12.5" cy="12.5" r="6" fill="#ffffff"/>
        <circle cx="12.5" cy="12.5" r="3" fill="#ff6720"/>
      </svg>
    `),
      iconSize: [25, 41],
      iconAnchor: [12, 41],
      popupAnchor: [1, -34],
    });

    // Store original bounds
    const allMarkers = branches.map((branch) =>
      window.L.marker([branch.lat, branch.lng]),
    );
    const group = new window.L.featureGroup(allMarkers);
    const originalBounds = group.getBounds().pad(0.1);

    // Add markers for each branch
    branches.forEach((branch) => {
      const marker = window.L.marker([branch.lat, branch.lng], {
        icon: orangeIcon,
      }).addTo(map);

      // Create popup
      const popup = window.L.popup({
        maxWidth: 320,
        className: "custom-popup",
      }).setContent(`
      <div style="min-width: 280px;">
        <div style="display: flex; gap: 12px; margin-bottom: 12px;">
          <div style="width: 100px; height: 80px; flex-shrink: 0; border-radius: 8px; overflow: hidden; background: #f5f5f5;">
            <img src="${branch.image}"
                 alt="${branch.name}"
                 style="width: 100%; height: 100%; object-fit: cover;"
                 onerror="this.src='https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=400&h=300&fit=crop'"/>
          </div>
          <div style="flex: 1; display: flex; flex-direction: column; justify-content: center;">
            <h3 style="margin: 0 0 6px 0; font-size: 15px; color: #333; font-weight: 600; line-height: 1.3;">${branch.name}</h3>
            <p style="margin: 0 0 4px 0; font-size: 12px; color: #666; line-height: 1.4;">${branch.address}</p>
            <a href="https://wa.me/${branch.phone.replace(/[^0-9]/g, "")}"
               target="_blank"
               rel="noopener noreferrer"
               style="margin: 0; font-size: 13px; color: #25D366; font-weight: 600; text-decoration: none; display: flex; align-items: center; gap: 6px; width: fit-content;">
              <svg style="width: 16px; height: 16px; fill: currentColor;" viewBox="0 0 448 512">
                <path d="M380.9 97.1C339 55.1 283.2 32 223.9 32c-122.4 0-222 99.6-222 222 0 39.1 10.2 77.3 29.6 111L0 480l117.7-30.9c32.4 17.7 68.9 27 106.1 27h.1c122.3 0 224.1-99.6 224.1-222 0-59.3-25.2-115-67.1-157zm-157 341.6c-33.2 0-65.7-8.9-94-25.7l-6.7-4-69.8 18.3L72 359.2l-4.4-7c-18.5-29.4-28.2-63.3-28.2-98.2 0-101.7 82.8-184.5 184.6-184.5 49.3 0 95.6 19.2 130.4 54.1 34.8 34.9 56.2 81.2 56.1 130.5 0 101.8-84.9 184.6-186.6 184.6zm101.2-138.2c-5.5-2.8-32.8-16.2-37.9-18-5.1-1.9-8.8-2.8-12.5 2.8-3.7 5.6-14.3 18-17.6 21.8-3.2 3.7-6.5 4.2-12 1.4-32.6-16.3-54-29.1-75.5-66-5.7-9.8 5.7-9.1 16.3-30.3 1.8-3.7.9-6.9-.5-9.7-1.4-2.8-12.5-30.1-17.1-41.2-4.5-10.8-9.1-9.3-12.5-9.5-3.2-.2-6.9-.2-10.6-.2-3.7 0-9.7 1.4-14.8 6.9-5.1 5.6-19.4 19-19.4 46.3 0 27.3 19.9 53.7 22.6 57.4 2.8 3.7 39.1 59.7 94.8 83.8 35.2 15.2 49 16.5 66.6 13.9 10.7-1.6 32.8-13.4 37.4-26.4 4.6-13 4.6-24.1 3.2-26.4-1.3-2.5-5-3.9-10.5-6.6z"/>
              </svg>
              ${branch.phone}
            </a>
          </div>
        </div>
        <div style="display: flex; gap: 8px;">
          <button
            onclick="window.location.href='tel:${branch.phone}'"
            style="flex: 1; background: #ff6720; color: white; border: none; padding: 10px 16px; border-radius: 6px; font-size: 13px; font-weight: 600; cursor: pointer;"
            onmouseover="this.style.background='#e55a1c'"
            onmouseout="this.style.background='#ff6720'">
            Contact Now
          </button>
          <button
            onclick="window.open('https://www.google.com/maps/dir/?api=1&destination=${branch.lat},${branch.lng}', '_blank')"
            style="flex: 1; background: transparent; color: #ff6720; border: 1px solid #ff6720; padding: 10px 16px; border-radius: 6px; font-size: 13px; font-weight: 600; cursor: pointer;"
            onmouseover="this.style.background='#ff6720'; this.style.color='white'"
            onmouseout="this.style.background='transparent'; this.style.color='#ff6720'">
            Get Directions
          </button>
        </div>
      </div>
    `);

      marker.bindPopup(popup);

      // Zoom on marker click
      marker.on("click", function () {
        map.setView([branch.lat, branch.lng], 16, {
          animate: true,
          duration: 1,
        });
      });

      // Reset view when popup closes
      popup.on("remove", function () {
        map.fitBounds(originalBounds, {
          animate: true,
          duration: 1,
        });
      });
    });

    // Fit map to show all markers
    map.fitBounds(originalBounds);
  };

  const handleShare = (platform) => {
    const currentUrl = window.location.href;
    const productTitle = product?.name || "Product";
    const shareText = `Check out this product: ${productTitle}`;

    switch (platform) {
      case "whatsapp":
        const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(
          `${shareText} ${currentUrl}`,
        )}`;
        window.open(whatsappUrl, "_blank");
        break;

      case "facebook":
        const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
          currentUrl,
        )}`;
        window.open(facebookUrl, "_blank");
        break;

      case "message":
        const messageUrl = `sms:?body=${encodeURIComponent(
          `${shareText} ${currentUrl}`,
        )}`;
        window.open(messageUrl);
        break;

      default:
        break;
    }

    setShowSharePopup(false);
  };

  const handleContactForQuote = () => {
    if (product) {
      window.location.href = `mailto:sales@xplod-group.com?subject=Quote Request for ${product.name}&body=Hi, I would like to request a quote for the ${product.name} (${product.sku || product.id}). Please provide pricing and availability information.`;
    }
    setShowSharePopup(false);
  };

  const handleBackToProducts = () => {
    // Navigate to search/products page
    navigate("/search");
  };

  const handleBackToCategory = () => {
    if (product.category_name) {
      const searchParams = new URLSearchParams();
      searchParams.set("category", product.category_name);
      navigate(`/search?${searchParams.toString()}`);
    } else {
      navigate("/search");
    }
  };

  const handleGoBack = () => {
    // First try to use browser history
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      // Fallback to products page if no history
      navigate("/search");
    }
  };

  const parseJSONField = (field, fallback = []) => {
    if (!field) return fallback;
    try {
      return Array.isArray(field) ? field : JSON.parse(field);
    } catch (err) {
      console.error("Error parsing JSON field:", err);
      return fallback;
    }
  };

  const getProductMedia = (product) => {
    const images = parseJSONField(product.images, []);

    const media = { images: [], videos: [] };

    if (images.length === 0) {
      media.images = [
        "https://images.unsplash.com/photo-1558618047-3c8c76ca7d13?w=500&h=500&fit=crop",
        "https://images.unsplash.com/photo-1416879595882-3373a0480b5b?w=500&h=500&fit=crop",
        "https://images.unsplash.com/photo-1504148455328-c376907d081c?w=500&h=500&fit=crop",
      ];
      return media;
    }

    if (images.length > 0 && typeof images[0] === "string") {
      media.images = images;
      return media;
    }

    if (images.length > 0 && typeof images[0] === "object") {
      images.forEach((item) => {
        if (item.is_video) {
          const video = {
            url: item.image_url || item.url,
            isYouTube: item.is_youtube || false,
            isFacebook: item.is_facebook || false,
            youtubeId: item.youtube_id || null,
            facebookId: item.facebook_id || null,
            altText: item.alt_text || "Product Video",
          };

          media.videos.push(video);
        } else {
          const imageUrl = item.image_url || item.url;
          if (imageUrl) {
            media.images.push(imageUrl);
          }
        }
      });
    }

    if (media.images.length === 0) {
      media.images = [
        "https://images.unsplash.com/photo-1558618047-3c8c76ca7d13?w=500&h=500&fit=crop",
        "https://images.unsplash.com/photo-1416879595882-3373a0480b5b?w=500&h=500&fit=crop",
        "https://images.unsplash.com/photo-1504148455328-c376907d081c?w=500&h=500&fit=crop",
      ];
    }

    return media;
  };

  const getProductImages = (product) => {
    const media = getProductMedia(product);
    return media.images;
  };

  const getYouTubeEmbedUrl = (videoId) => {
    return `https://www.youtube.com/embed/${videoId}`;
  };

  const getFacebookEmbedUrl = (url) => {
    // Facebook embed URLs need the original URL encoded
    return `https://www.facebook.com/plugins/video.php?href=${encodeURIComponent(url)}&show_text=false&width=560&height=315`;
  };

  const getProductFeatures = (product) => {
    const features = parseJSONField(product.features, []);

    if (features.length > 0 && typeof features[0] === "string") {
      return features.map((feature) => ({ title: feature, description: "" }));
    }

    if (features.length > 0 && typeof features[0] === "object") {
      return features
        .map((feature) => {
          // Handle {heading, content} structure
          if (feature.heading && feature.content) {
            const content =
              typeof feature.content === "object"
                ? JSON.stringify(feature.content)
                : feature.content;
            return { title: feature.heading, description: content };
          }
          // Handle {title, description} structure
          if (feature.title && feature.description) {
            const description =
              typeof feature.description === "object"
                ? JSON.stringify(feature.description)
                : feature.description;
            return { title: feature.title, description: description };
          }
          // Handle other structures
          const title =
            feature.title || feature.name || feature.text || "Feature";
          const description =
            feature.description || feature.content || feature.value || "";
          return { title, description };
        })
        .filter((item) => item.title && item.title.trim() !== "");
    }

    return [];
  };

  const getProductSpecifications = (product) => {
    const specs = parseJSONField(
      product.technical_details || product.specifications,
      {},
    );

    // If specs is an array of objects with heading/content structure
    if (Array.isArray(specs)) {
      const specsObject = {};
      specs.forEach((spec) => {
        // Handle {heading, content} structure
        if (spec.heading && spec.content) {
          specsObject[spec.heading] =
            typeof spec.content === "object"
              ? JSON.stringify(spec.content)
              : spec.content;
        }
        // Handle {name, value} structure
        else if (spec.name && spec.value) {
          specsObject[spec.name] =
            typeof spec.value === "object"
              ? JSON.stringify(spec.value)
              : spec.value;
        }
        // Handle other structures
        else if (spec.title || spec.label) {
          const key = spec.title || spec.label;
          const value = spec.description || spec.value || spec.text || "";
          specsObject[key] =
            typeof value === "object" ? JSON.stringify(value) : value;
        }
      });
      return specsObject;
    }

    // If specs is already an object
    if (typeof specs === "object" && !Array.isArray(specs)) {
      // Ensure all values are strings
      const cleanedSpecs = {};
      Object.entries(specs).forEach(([key, value]) => {
        cleanedSpecs[key] =
          typeof value === "object" && value !== null
            ? JSON.stringify(value)
            : value;
      });
      return cleanedSpecs;
    }

    return specs;
  };

  const getBriefDescription = (product) => {
    const briefDesc = product.brief_description;

    if (!briefDesc) return null;

    // Handle object brief descriptions
    if (typeof briefDesc === "object" && briefDesc !== null) {
      return String(
        briefDesc.text ||
          briefDesc.content ||
          briefDesc.description ||
          briefDesc.value ||
          JSON.stringify(briefDesc),
      );
    }

    return String(briefDesc);
  };

  const getCustomSections = (product) => {
    const customSections = parseJSONField(product.custom_sections, []);

    if (!Array.isArray(customSections) || customSections.length === 0) {
      return [];
    }

    return customSections
      .map((section, index) => {
        // Handle different section structures
        const title =
          section.title ||
          section.heading ||
          section.name ||
          `Section ${index + 1}`;
        const content =
          section.content ||
          section.description ||
          section.text ||
          section.value;

        return {
          id: index,
          title: String(title),
          content:
            typeof content === "object" && content !== null
              ? String(
                  content.text ||
                    content.description ||
                    JSON.stringify(content),
                )
              : String(content || ""),
        };
      })
      .filter((section) => section.content.trim() !== "");
  };

  const getProductPrice = (product) => {
    if (product.price) {
      return `₹${parseFloat(product.price).toLocaleString("en-IN")}`;
    }
    return "Contact Now";
  };

  const isInStock = (product) => {
    return product.inventory_quantity > 0 || product.status === "active";
  };

  if (loading) {
    return (
      <ProductDetailWrapper>
        <Container>
          <LoadingSpinner>Loading product details...</LoadingSpinner>
        </Container>
      </ProductDetailWrapper>
    );
  }

  if (error || !product) {
    return (
      <ProductDetailWrapper>
        <Container>
          <NotFound>
            <h2>Product Not Found</h2>
            <p>
              The product you're looking for doesn't exist or has been removed.
            </p>
            <BackLink to="/">Return to Home</BackLink>
          </NotFound>
        </Container>
      </ProductDetailWrapper>
    );
  }

  const productMedia = getProductMedia(product);
  const productImages = productMedia.images;
  const productVideos = productMedia.videos;
  const productFeatures = getProductFeatures(product);
  const productSpecifications = getProductSpecifications(product);
  const inStock = isInStock(product);

  const breadcrumbItems = [
    { name: 'Home', url: '/' },
    { name: 'Products', url: '/search' },
  ];

  if (product.category_name) {
    breadcrumbItems.push({ name: product.category_name, url: `/collections/${product.category_name.toLowerCase()}` });
  }

  breadcrumbItems.push({ name: product.name, url: `/products/${product.id}` });

  return (
    <ProductDetailWrapper>
      <SEO
        title={`${product.name} | XPLOD-GROUPS - ${product.brand || 'Professional'} ${product.category_name || 'Tool'}`}
        description={generateMetaDescription(product)}
        keywords={generateMetaKeywords(product, product.category_name)}
        canonical={`/products/${product.id}`}
        image={productImages[0]}
        type="product"
        structuredData={[generateProductSchema(product), generateBreadcrumbSchema(breadcrumbItems)]}
      />
      <Container>
        <Breadcrumb>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <Link to="/">Home</Link>
            <span className="separator">›</span>
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                handleGoBack();
              }}
              style={{
                color: "#666",
                textDecoration: "none",
                cursor: "pointer",
              }}
              onMouseEnter={(e) => (e.target.style.color = "#ff6720")}
              onMouseLeave={(e) => (e.target.style.color = "#666")}
            >
              Products
            </a>
            <span className="separator">›</span>
            {product.category_name && (
              <>
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    handleBackToCategory();
                  }}
                  style={{
                    color: "#666",
                    textDecoration: "none",
                    cursor: "pointer",
                  }}
                  onMouseEnter={(e) => (e.target.style.color = "#ff6720")}
                  onMouseLeave={(e) => (e.target.style.color = "#666")}
                >
                  {String(product.category_name)}
                </a>
                <span className="separator">›</span>
              </>
            )}
            <span>{String(product.name)}</span>
          </div>
        </Breadcrumb>

        <MainContent>
          <ImageSection>
            <ThumbnailList>
              {/* Then show image thumbnails */}
              {productImages
                .slice(0, 6 - productVideos.length)
                .map((image, index) => (
                  <Thumbnail
                    key={`image-${index}`}
                    active={selectedImageIndex === index + productVideos.length}
                    onClick={() =>
                      setSelectedImageIndex(index + productVideos.length)
                    }
                  >
                    <img
                      src={image}
                      alt={`${product.name} ${index + 1}`}
                      onError={(e) => {
                        e.target.src =
                          "https://images.unsplash.com/photo-1558618047-3c8c76ca7d13?w=500&h=500&fit=crop";
                      }}
                    />
                  </Thumbnail>
                ))}
              {/* Show video thumbnails second */}
              {productVideos.map((video, index) => (
                <Thumbnail
                  key={`video-${index}`}
                  active={
                    selectedImageIndex === index &&
                    selectedImageIndex < productVideos.length
                  }
                  onClick={() => setSelectedImageIndex(index)}
                >
                  {video.isYouTube ? (
                    <img
                      src={`https://img.youtube.com/vi/${video.youtubeId}/maxresdefault.jpg`}
                      alt={video.altText}
                      onError={(e) => {
                        e.target.src = `https://img.youtube.com/vi/${video.youtubeId}/hqdefault.jpg`;
                      }}
                    />
                  ) : video.isFacebook ? (
                    <img
                      src="https://upload.wikimedia.org/wikipedia/commons/thumb/5/51/Facebook_f_logo_%282019%29.svg/1200px-Facebook_f_logo_%282019%29.svg.png"
                      alt={video.altText}
                      style={{ background: "#1877F2", padding: "20px" }}
                    />
                  ) : (
                    <video src={video.url} alt={video.altText} muted />
                  )}
                </Thumbnail>
              ))}
            </ThumbnailList>

            <MainImageContainer>
              <ActionButtons1>
                <ShareButton onClick={() => setShowSharePopup(true)}>
                  <FiShare2 size={16} />
                  Share
                </ShareButton>
              </ActionButtons1>
              {selectedImageIndex < productVideos.length ? (
                // Display video
                <VideoContainer>
                  {(() => {
                    const video = productVideos[selectedImageIndex];
                    if (video.isYouTube) {
                      return (
                        <iframe
                          src={getYouTubeEmbedUrl(video.youtubeId)}
                          title={video.altText}
                          allowFullScreen
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        />
                      );
                    } else if (video.isFacebook) {
                      return (
                        <iframe
                          src={getFacebookEmbedUrl(video.url)}
                          title={video.altText}
                          allowFullScreen
                          allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
                        />
                      );
                    } else {
                      return (
                        <video
                          src={video.url}
                          controls
                          controlsList="nodownload"
                        />
                      );
                    }
                  })()}
                </VideoContainer>
              ) : (
                // Display image
                <MainImage>
                  <img
                    src={
                      productImages[
                        selectedImageIndex - productVideos.length
                      ] || productImages[0]
                    }
                    alt={product.name}
                    onError={(e) => {
                      e.target.src =
                        "https://images.unsplash.com/photo-1558618047-3c8c76ca7d13?w=500&h=500&fit=crop";
                    }}
                  />
                </MainImage>
              )}

            </MainImageContainer>
            <ActionButtonsSection>
              <ContactButton onClick={handleContactForQuote}>
                Contact Now
              </ContactButton>
              <FindBranchButton onClick={() => setShowBranchMap(true)}>
                Find Branch
              </FindBranchButton>
            </ActionButtonsSection>
          </ImageSection>

          <ProductInfo>
            <ProductHeader>
              <TitleSection>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px', flexWrap: 'wrap' }}>
                  <ProductBrand style={{ margin: 0 }}>{String(product.brand || "STIHL")}</ProductBrand>
                  <span style={{ color: '#ff6720', fontSize: '18px', fontWeight: 'bold' }}><IoIosArrowForward /></span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <SellerName style={{ fontSize: '14px', margin: 0 }}>XPLOD-GROUPS</SellerName>
                    <SellerBadge
                      src="https://www.stihl.in/content/dam/stihl/system-assets/icons/dealer-locator/IN-Platinum-Plus-Badge.png"
                      alt="Platinum Plus Badge"
                    />
                  </div>
                </div>
                <ProductTitle>{String(product.name)}</ProductTitle>
                {product.type && (
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '8px',
                    marginBottom: '20px',
                    borderRadius: '6px',
                    width: 'fit-content'
                  }}>
                    {(() => {
                      const type = product.type.toLowerCase();
                      
                      if (type.includes('petrol') || type.includes('gas')) {
                        return <FaGasPump size={18} style={{ color: '#ff6720' }} />;
                      } else if (type.includes('cordless') || type.includes('battery')) {
                        return <FaBatteryFull size={18} style={{ color: '#4caf50' }} />;
                      } else if (type.includes('corded') || type.includes('electric')) {
                        return <FaBolt size={18} style={{ color: '#2196f3' }} />;
                      }
                      return <FaTag size={18} style={{ color: '#ff6720' }} />;
                    })()}
                    <span style={{ 
                      fontSize: '14px', 
                      fontWeight: '600',
                      color: '#333'
                    }}>
                      {product.type}
                    </span>
                  </div>
                )}
                {getBriefDescription(product) && (
                  <HighlightsSection>
                    <div style={{ color: "#666", lineHeight: "1.6" }}>
                      {getBriefDescription(product)}
                    </div>
                  </HighlightsSection>
                )}
              </TitleSection>

              <ActionButtons>
                <ShareButton onClick={() => setShowSharePopup(true)}>
                  <FiShare2 size={16} />
                  Share
                </ShareButton>
              </ActionButtons>
            </ProductHeader>

            {showSharePopup && (
              <SharePopup onClick={() => setShowSharePopup(false)}>
                <ShareModal onClick={(e) => e.stopPropagation()}>
                  <ShareHeader>
                    <ShareTitle>Share this product</ShareTitle>
                    <CloseButton onClick={() => setShowSharePopup(false)}>
                      <FiX />
                    </CloseButton>
                  </ShareHeader>
                  <ShareOptions>
                    <ShareOption
                      className="whatsapp"
                      onClick={() => handleShare("whatsapp")}
                    >
                      <ShareIcon>
                        <FaWhatsapp color="#25D366" />
                      </ShareIcon>
                      WhatsApp
                    </ShareOption>
                    <ShareOption
                      className="facebook"
                      onClick={() => handleShare("facebook")}
                    >
                      <ShareIcon>
                        <FaFacebook color="#1877F2" />
                      </ShareIcon>
                      Facebook
                    </ShareOption>

                    <ShareOption
                      className="message"
                      onClick={() => handleShare("message")}
                    >
                      <ShareIcon>
                        <FaEnvelope color="#F97316" />
                      </ShareIcon>
                      Message
                    </ShareOption>
                  </ShareOptions>
                </ShareModal>
              </SharePopup>
            )}

            <PaymentSection>
              <PaymentTitle>Easy Payment Options</PaymentTitle>
              <PaymentOptions>
                <PaymentOption>
                  <PaymentOptionWithIcon>
                    <PaymentIcon>
                      <img
                        src="https://play-lh.googleusercontent.com/DbepofsHLK7fTQmiQi9KurqbL1VvVJEAJ0AOX8CejdsgygCTH_0K4kG9JLmcKl3MkN0K"
                        alt="Bajaj Finance"
                      />
                    </PaymentIcon>
                    <span>Bajaj Finance</span>
                  </PaymentOptionWithIcon>
                  <span className="value" style={{ color: "#007bff" }}>
                    Available
                  </span>
                </PaymentOption>
                <PaymentOption>
                  <PaymentOptionWithIcon>
                    <PaymentIcon style={{ backgroundColor: "#28a745" }}>
                      <FaUniversity color="white" size={14} />
                    </PaymentIcon>
                    <span>Private Finance</span>
                  </PaymentOptionWithIcon>
                  <span className="value" style={{ color: "#007bff" }}>
                    Available
                  </span>
                </PaymentOption>
              </PaymentOptions>
            </PaymentSection>

            {Object.keys(productSpecifications).length > 0 && (
              <HighlightsSection>
                <HighlightsTitle>Technical details</HighlightsTitle>
                <TechnicalSpecsTable>
                  {Object.entries(productSpecifications)
                    .slice(0, 6)
                    .map(([key, value]) => {
                      // Safely convert value to string
                      const displayValue =
                        typeof value === "object" && value !== null
                          ? value.heading ||
                            value.content ||
                            value.text ||
                            JSON.stringify(value)
                          : String(value);

                      return (
                        <SpecRow key={key}>
                          <SpecLabel>{String(key)}</SpecLabel>
                          <SpecValue>{String(displayValue)}</SpecValue>
                        </SpecRow>
                      );
                    })}
                </TechnicalSpecsTable>
              </HighlightsSection>
            )}

            {(product.description ||
              product.brief_description ||
              product.short_description) && (
              <HighlightsSection>
                <HighlightsTitle>Description</HighlightsTitle>
                <div style={{ color: "#666", lineHeight: "1.6" }}>
                  {(() => {
                    const desc =
                      product.description ||
                      product.brief_description ||
                      product.short_description;

                    // Handle object descriptions
                    if (typeof desc === "object" && desc !== null) {
                      // Try to extract meaningful text from object
                      return String(
                        desc.text ||
                          desc.content ||
                          desc.description ||
                          desc.value ||
                          JSON.stringify(desc),
                      );
                    }

                    return String(desc || "NA");
                  })()}
                </div>
              </HighlightsSection>
            )}

            {productFeatures.length > 0 && (
              <CollapsibleSection>
                <CollapsibleHeader
                  onClick={() => setShowDetailsFeatures(!showDetailsFeatures)}
                  isOpen={showDetailsFeatures}
                >
                  <CollapsibleTitle>Details & Features</CollapsibleTitle>
                  <CollapsibleIcon isOpen={showDetailsFeatures}>
                    +
                  </CollapsibleIcon>
                </CollapsibleHeader>
                <CollapsibleContent isOpen={showDetailsFeatures}>
                  <FeatureGrid>
                    {productFeatures.map((feature, index) => (
                      <FeatureItem key={index}>
                        <FeatureTitle>{feature.title}</FeatureTitle>
                        {feature.description && (
                          <FeatureDescription>
                            {feature.description}
                          </FeatureDescription>
                        )}
                      </FeatureItem>
                    ))}
                  </FeatureGrid>
                </CollapsibleContent>
              </CollapsibleSection>
            )}

            {getCustomSections(product).length > 0 && (
              <>
                {getCustomSections(product).map((section) => (
                  <HighlightsSection key={section.id}>
                    <HighlightsTitle>{section.title}</HighlightsTitle>
                    <div style={{ color: "#666", lineHeight: "1.6" }}>
                      {section.content}
                    </div>
                  </HighlightsSection>
                ))}
              </>
            )}
          </ProductInfo>
        </MainContent>

        <CarouselWrapper>
          <RelatedTitle>Related Products</RelatedTitle>
          
          {/* Desktop Carousel */}
          <DesktopCarouselOnly>
            <Carousel indicators={true} controls={true}>
              {categoryProducts.length > 0 ? (
                Array(Math.ceil(categoryProducts.length / 4))
                  .fill()
                  .map((_, index) => (
                    <Carousel.Item key={index}>
                      <CarouselItemWrapper>
                        {categoryProducts
                          .slice(index * 4, (index + 1) * 4)
                          .map((relatedProduct) => (
                            <CarouselProductCard key={relatedProduct.id}>
                              <RelatedProductImage>
                                <img
                                  src={
                                    relatedProduct.images?.[0]?.image_url ||
                                    relatedProduct.image ||
                                    "https://images.unsplash.com/photo-1558618047-3c8c76ca7d13?w=280&h=200&fit=crop"
                                  }
                                  alt={relatedProduct.images?.[0]?.alt_text || relatedProduct.name}
                                  onError={(e) => {
                                    e.target.src = "https://images.unsplash.com/photo-1558618047-3c8c76ca7d13?w=280&h=200&fit=crop";
                                  }}
                                />
                              </RelatedProductImage>
                              <RelatedProductInfo>
                                <RelatedProductName>{String(relatedProduct.name)}</RelatedProductName>
                                <RelatedProductDescription>
                                  {(
                                    relatedProduct.brief_description ||
                                    relatedProduct.description ||
                                    `High-quality ${relatedProduct.name.toLowerCase()} with professional features.`
                                  )
                                    .split(" ")
                                    .slice(0, 15)
                                    .join(" ") + "..."}
                                </RelatedProductDescription>
                                <RelatedViewButton to={`/products/${relatedProduct.id}`}>
                                  View Details
                                </RelatedViewButton>
                              </RelatedProductInfo>
                            </CarouselProductCard>
                          ))}
                      </CarouselItemWrapper>
                    </Carousel.Item>
                  ))
              ) : (
                <NoRelatedProducts>No related products found in this category.</NoRelatedProducts>
              )}
            </Carousel>
          </DesktopCarouselOnly>

          {/* Mobile Scroll */}
          <MobileCarouselWrapper>
            <MobileScrollContainer ref={relatedScrollRef} onScroll={handleRelatedScroll}>
              {categoryProducts.length > 0 ? (
                categoryProducts.map((relatedProduct) => (
                  <MobileProductCard key={relatedProduct.id}>
                    <RelatedProductImage>
                      <img
                        src={
                          relatedProduct.images?.[0]?.image_url ||
                          relatedProduct.image ||
                          "https://images.unsplash.com/photo-1558618047-3c8c76ca7d13?w=280&h=200&fit=crop"
                        }
                        alt={relatedProduct.images?.[0]?.alt_text || relatedProduct.name}
                        onError={(e) => {
                          e.target.src = "https://images.unsplash.com/photo-1558618047-3c8c76ca7d13?w=280&h=200&fit=crop";
                        }}
                      />
                    </RelatedProductImage>
                    <RelatedProductInfo>
                      <RelatedProductName>{String(relatedProduct.name)}</RelatedProductName>
                      <RelatedProductDescription>
                        {(
                          relatedProduct.brief_description ||
                          relatedProduct.description ||
                          `High-quality ${relatedProduct.name.toLowerCase()} with professional features.`
                        )
                          .split(" ")
                          .slice(0, 15)
                          .join(" ") + "..."}
                      </RelatedProductDescription>
                      <RelatedViewButton to={`/products/${relatedProduct.id}`}>
                        View Details
                      </RelatedViewButton>
                    </RelatedProductInfo>
                  </MobileProductCard>
                ))
              ) : (
                <NoRelatedProducts>No related products found in this category.</NoRelatedProducts>
              )}
            </MobileScrollContainer>
            
            {categoryProducts.length > 0 && (
              <MobileProgressBarContainer>
                <MobileProgressBar>
                  <MobileProgressFill progress={relatedScrollProgress} />
                </MobileProgressBar>
                <MobileArrowButton onClick={scrollRelatedLeft} disabled={!relatedShowLeftArrow}>
                  <AiOutlineArrowLeft />
                </MobileArrowButton>
                <MobileArrowButton onClick={scrollRelatedRight} disabled={!relatedShowRightArrow}>
                  <AiOutlineArrowRight />
                </MobileArrowButton>
              </MobileProgressBarContainer>
            )}
          </MobileCarouselWrapper>
        </CarouselWrapper>

        <CarouselWrapper>
          <YouMayAlsoLikeTitle>You May Also Like</YouMayAlsoLikeTitle>
          
          {/* Desktop Carousel */}
          <DesktopCarouselOnly>
            <Carousel indicators={true} controls={true}>
              {allProducts.length > 0 ? (
                Array(Math.ceil(allProducts.length / 4))
                  .fill()
                  .map((_, index) => (
                    <Carousel.Item key={index}>
                      <CarouselItemWrapper>
                        {allProducts
                          .slice(index * 4, (index + 1) * 4)
                          .map((product) => (
                            <CarouselProductCard key={product.id}>
                              <YouMayAlsoLikeImage>
                                <img
                                  src={
                                    product.images?.[0]?.image_url ||
                                    product.image ||
                                    "https://images.unsplash.com/photo-1558618047-3c8c76ca7d13?w=280&h=200&fit=crop"
                                  }
                                  alt={product.images?.[0]?.alt_text || product.name}
                                  onError={(e) => {
                                    e.target.src = "https://images.unsplash.com/photo-1558618047-3c8c76ca7d13?w=280&h=200&fit=crop";
                                  }}
                                />
                              </YouMayAlsoLikeImage>
                              <YouMayAlsoLikeInfo>
                                <YouMayAlsoLikeName>{String(product.name)}</YouMayAlsoLikeName>
                                <YouMayAlsoLikeDescription>
                                  {(
                                    product.brief_description ||
                                    product.description ||
                                    `High-quality ${product.name.toLowerCase()} with professional features.`
                                  )
                                    .split(" ")
                                    .slice(0, 15)
                                    .join(" ") + "..."}
                                </YouMayAlsoLikeDescription>
                                <YouMayAlsoLikeViewButton to={`/products/${product.id}`}>
                                  View Details
                                </YouMayAlsoLikeViewButton>
                              </YouMayAlsoLikeInfo>
                            </CarouselProductCard>
                          ))}
                      </CarouselItemWrapper>
                    </Carousel.Item>
                  ))
              ) : (
                <NoYouMayAlsoLikeProducts>No other products found.</NoYouMayAlsoLikeProducts>
              )}
            </Carousel>
          </DesktopCarouselOnly>

          {/* Mobile Scroll */}
          <MobileCarouselWrapper>
            <MobileScrollContainer ref={alsoLikeScrollRef} onScroll={handleAlsoLikeScroll}>
              {allProducts.length > 0 ? (
                allProducts.map((product) => (
                  <MobileProductCard key={product.id}>
                    <YouMayAlsoLikeImage>
                      <img
                        src={
                          product.images?.[0]?.image_url ||
                          product.image ||
                          "https://images.unsplash.com/photo-1558618047-3c8c76ca7d13?w=280&h=200&fit=crop"
                        }
                        alt={product.images?.[0]?.alt_text || product.name}
                        onError={(e) => {
                          e.target.src = "https://images.unsplash.com/photo-1558618047-3c8c76ca7d13?w=280&h=200&fit=crop";
                        }}
                      />
                    </YouMayAlsoLikeImage>
                    <YouMayAlsoLikeInfo>
                      <YouMayAlsoLikeName>{String(product.name)}</YouMayAlsoLikeName>
                      <YouMayAlsoLikeDescription>
                        {(
                          product.brief_description ||
                          product.description ||
                          `High-quality ${product.name.toLowerCase()} with professional features.`
                        )
                          .split(" ")
                          .slice(0, 15)
                          .join(" ") + "..."}
                      </YouMayAlsoLikeDescription>
                      <YouMayAlsoLikeViewButton to={`/products/${product.id}`}>
                        View Details
                      </YouMayAlsoLikeViewButton>
                    </YouMayAlsoLikeInfo>
                  </MobileProductCard>
                ))
              ) : (
                <NoYouMayAlsoLikeProducts>No other products found.</NoYouMayAlsoLikeProducts>
              )}
            </MobileScrollContainer>
            
            {allProducts.length > 0 && (
              <MobileProgressBarContainer>
                <MobileProgressBar>
                  <MobileProgressFill progress={alsoLikeScrollProgress} />
                </MobileProgressBar>
                <MobileArrowButton onClick={scrollAlsoLikeLeft} disabled={!alsoLikeShowLeftArrow}>
                  <AiOutlineArrowLeft />
                </MobileArrowButton>
                <MobileArrowButton onClick={scrollAlsoLikeRight} disabled={!alsoLikeShowRightArrow}>
                  <AiOutlineArrowRight />
                </MobileArrowButton>
              </MobileProgressBarContainer>
            )}
          </MobileCarouselWrapper>
        </CarouselWrapper>
      </Container>

      {showBranchMap && (
        <BranchMapModal onClick={() => setShowBranchMap(false)}>
          <BranchMapContent onClick={(e) => e.stopPropagation()}>
            <BranchMapHeader>
              <BranchMapTitle>Our Branch Locations</BranchMapTitle>
              <BranchMapClose onClick={() => setShowBranchMap(false)}>
                <FiX />
              </BranchMapClose>
            </BranchMapHeader>
            <BranchMapBodyContainer>
              <BranchMapContainer>
                <div
                  id="branch-map"
                  style={{ width: "100%", height: "100%" }}
                />
              </BranchMapContainer>
              <BranchList>
                {branches.map((branch) => (
                  <BranchItem key={branch.id}>
                    <BranchCardTop>
                      <BranchImageContainer>
                        <img
                          src={branch.image}
                          alt={branch.name}
                          onError={(e) => {
                            e.target.src =
                              "https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=400&h=300&fit=crop";
                          }}
                        />
                      </BranchImageContainer>
                      <BranchInfoContainer>
                        <BranchName>{branch.name}</BranchName>
                        <BranchAddress>{branch.address}</BranchAddress>
                        <BranchPhone
                          href={`https://wa.me/${branch.phone.replace(/[^0-9]/g, "")}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <FaWhatsapp />
                          {branch.phone}
                        </BranchPhone>
                      </BranchInfoContainer>
                    </BranchCardTop>
                    <BranchButtonsContainer>
                      <BranchActionButton
                        primary
                        onClick={() =>
                          (window.location.href = `tel:${branch.phone}`)
                        }
                      >
                        Contact Now
                      </BranchActionButton>
                      <BranchActionButton
                        onClick={() =>
                          window.open(
                            `https://www.google.com/maps/dir/?api=1&destination=${branch.lat},${branch.lng}&travelmode=driving`,
                            "_blank",
                          )
                        }
                      >
                        Get Directions
                      </BranchActionButton>
                    </BranchButtonsContainer>
                  </BranchItem>
                ))}
              </BranchList>
            </BranchMapBodyContainer>
          </BranchMapContent>
        </BranchMapModal>
      )}
    </ProductDetailWrapper>
  );
};

export default ProductDetailPage;
